'use strict'
import _ from 'lodash'
import moment from 'moment'

// NOTE
// Les states avec un _ doivent être accédés via des getters
// Les mutations avec un _ doivent être appelées uniquement depuis des actions

const aggregationModeFromFrameSize = (state) => {
  if (state.frameSize === null) return 'dd'
  if (state.frameSize.asYears() >= 25) return 'yyyy'
  if (state.frameSize.asYears() < 25 && state.frameSize.asYears() > 5) return 'mm'
  if (state.frameSize.asMonths() <= 60 && state.frameSize.asMonths() > 1) return 'dd'
  if (state.frameSize.asMonths() <= 1) return 'raw'
}

const defaultDateFrom = moment.utc().subtract(1, 'years').set('date', 1).set('month', 9)
const defaultDateTo =  moment.utc().set('date', 30).set('month', 8)

export default {
  state: {
    dateFrom: defaultDateFrom,
    dateTo: defaultDateTo,
    aggregationMode: 'dd',
    events: [],
    selectedTimelineId: null,
    frameSize: moment.duration(defaultDateTo.clone().diff(defaultDateFrom)),
    userUtcOffset: 60,
    timeResolution: 'MM'
  },
  getters: {
    timelines (state) {
      let toReturn = _.groupBy(state.events, 'timelineId')
      return Object.entries(toReturn).map((d) => d[1]).sort((a, b) => a[0].order - b[0].order)
    },
    aggregationModeFromFrameSize (state) {
      return aggregationModeFromFrameSize(state)
    },
    utcOffsetDisplay (state) {
      switch (state.userUtcOffset) {
        case 60:
        default:
          return 'UTC+1'
      }
    },
    availableHydrologicalYears (state) {
      let toReturn = [state.currentHydrologicalYear]
      if (state.events.length === 0) return toReturn
      let minDate = state.events[0].start
      state.events.forEach((event) => {
        if (event.start.isBefore(minDate)) minDate = event.start
      })
      toReturn = _.reverse(_.range(minDate.year(), moment.utc().year() + 1))
      return toReturn
    },
    firstValueDate (state) {
      let toReturn = moment.utc()
      if (state.events.length === 0) return toReturn
      toReturn = state.events[0].start
      state.events.forEach((event) => {
        if (event.start.isBefore(toReturn)) toReturn = event.start
      })
      return toReturn
    },
    lastValueDate (state) {
      let toReturn = moment.utc()
      if (state.events.length === 0) return toReturn
      toReturn = state.events[0].end
      state.events.forEach((event) => {
        if (event.end.isAfter(toReturn)) toReturn = event.end
      })
      return toReturn
    },
    currentHydrologicalYear (state) {
      return moment.utc(state.dateTo).year()
    },
    dateMiddle (state) {
      let halfFrameSize = state.frameSize.asMonths() / 2
      return moment.utc(state.dateFrom).add(halfFrameSize, 'months')
    }
  },
  mutations: {
    setSelectedTimelineId (state, id) {
      state.selectedTimelineId = id
    },
    setEvents (state, events) {
      state.events = events
      if (state.events.length > 0) state.selectedTimelineId = state.events[0].timelineId
    },
    cleanEvents (state) {
      state.events = []
      state.selectedTimelineId = null
    },
    addEvents (state, events) {
      state.events = state.events.concat(events)
    },
    removeEvents (state, eventIds) {
      _.remove(state.events, (e) => eventIds.includes(e.id))
    },
    setTimeframe (state, domain) {
      state.dateFrom = moment(domain[0])
      state.dateTo = moment(domain[1])
      let newFrameSize = moment.duration(state.dateTo.diff(state.dateFrom))
      if (state.frameSize !== null && state.frameSize.asMonths().toFixed(0) !== newFrameSize.asMonths().toFixed(0)) state.aggregationMode = aggregationModeFromFrameSize(state)
      state.frameSize = newFrameSize
    },
    setAggregationMode (state, aggregationMode) {
      state.aggregationMode = aggregationMode
    },
    setCurrentHydrologicalYear (state, hydrologicalYear) {
      state.currentHydrologicalYear = hydrologicalYear
    },
    _setDateFrom (state, dateFrom) {
      state.dateFrom = dateFrom
    },
    _setDateTo (state, dateTo) {
      state.dateTo = dateTo
    },
    _setFrameSize (state, frameSize) {
      state.frameSize = frameSize
    },
    _setAggregationMode (state, aggregationMode) {
      state.aggregationMode = aggregationMode
    }
  },
  actions: {
    updateTimeframeForLayer ({ commit }, layers) {
      return new Promise((resolve, reject) => {
        let toFetch = []
        layers.forEach((layer) => {
          toFetch.push(layer.getTimeDimension())
        })

        Promise.all(toFetch).then((data) => {
          if (data.length === 0) {
            commit('cleanEvents')
            return resolve(null)
          }
          // Calculs des bornes dateFrom / dateTo maximal pour tous les layers
          let dateFrom = _.head(data).dateFrom
          let dateTo = _.head(data).dateTo
          data.forEach((timeDimension) => {
            if (timeDimension.dateFrom.isBefore(dateFrom)) dateFrom = timeDimension.dateFrom
            if (timeDimension.dateTo.isAfter(dateTo)) dateTo = timeDimension.dateTo
          })

          // Cas de bord:   Tous les layers ont le paramètre showOnlyMonth à true.
          // Utilité:       Pouvoir afficher et utiliser la carte "Cartes de références".
          // Pourquoi donc: Pour faciliter l'utilisation de ces données, les cartes de références sont utilisées comme des layers temporelles.
          // Hors:          Seul les mois sont important pour ces données car, peu importe l'année, les mois de références reste... une référence pour ce mois-ci...
          // Mais:          pour pouvoir les utiliser en parallèle avec les cartes mensuelles, les bornes temporelles de ces cartes de références
          //                sont définies suivant les bornes des cartes mensuelles
          // Donc:          Si on affiche uniquement des cartes de références, il faut limiter la borne déclarée par le mapserver à une seule année
          let showOnlyMonth = !!layers.map((layer) => layer.geoserverConfig.showMonthOnly).reduce((prev, curr) => prev && curr, true)
          if (showOnlyMonth) {
            dateTo = dateTo.year(dateFrom.year())
          }

          let events = [{
            id: 1,
            timelineId: 1,
            timelineName: '',
            eventName: '',
            start: dateFrom.utcOffset(0, true), // HACK: On "écrase" la timezone pour les données de la timeline: les données sont notés UTC mais comporte déjà le changement userUtcOffset
            end: dateTo.utcOffset(0, true),   // HACK: On "écrase" la timezone pour les données de la timeline: les données sont notés UTC mais comporte déjà le changement userUtcOffset
            color: '#184aa0',
            order: 1
          }]

          commit('setEvents', events)
          resolve({
            dateFrom,
            dateTo,
            timeResolution: layers[0].geoserverConfig.timeResolution
          })
        }).catch(reject)
      })
    },
  }
}
